import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@/shared/config/env';
import { ApiError, type ApiResponse } from './types';
import { ENDPOINTS } from './endpoints';
import { handleMockRequest } from './mocks/handlers';

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let getAccessToken: () => string | null = () => null;
let onUnauthorized: () => void = () => {};
let onTokenRefreshed: (token: string) => void = () => {};

export const configureApiClient = (params: {
  getAccessToken: () => string | null;
  onUnauthorized: () => void;
  onTokenRefreshed: (token: string) => void;
}) => {
  getAccessToken = params.getAccessToken;
  onUnauthorized = params.onUnauthorized;
  onTokenRefreshed = params.onTokenRefreshed;
};

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.VITE_API_URL,
  withCredentials: true,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    const headers = config.headers ?? new AxiosHeaders();
    if (headers instanceof AxiosHeaders) {
      headers.set('Authorization', `Bearer ${token}`);
    } else {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
    config.headers = headers;
  }
  return config;
});

let refreshPromise: Promise<string | null> | null = null;

const performRefresh = async (): Promise<string | null> => {
  try {
    const response = await axios.post<ApiResponse<{ accessToken: string }>>(
      `${env.VITE_API_URL}${ENDPOINTS.AUTH.REFRESH}`,
      {},
      { withCredentials: true, timeout: 15_000 },
    );
    if (response.data?.success && response.data.data?.accessToken) {
      const token = response.data.data.accessToken;
      onTokenRefreshed(token);
      return token;
    }
    return null;
  } catch {
    return null;
  }
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const originalRequest = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    const isRefreshCall = originalRequest?.url?.includes(ENDPOINTS.AUTH.REFRESH);
    const isLoginCall = originalRequest?.url?.includes(ENDPOINTS.AUTH.LOGIN);

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isRefreshCall &&
      !isLoginCall
    ) {
      originalRequest._retry = true;
      if (!refreshPromise) {
        refreshPromise = performRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        const headers = originalRequest.headers ?? new AxiosHeaders();
        if (headers instanceof AxiosHeaders) {
          headers.set('Authorization', `Bearer ${newToken}`);
        } else {
          (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
        }
        originalRequest.headers = headers;
        return apiClient.request(originalRequest);
      }
      onUnauthorized();
    }

    const payload = error.response?.data;
    if (payload && typeof payload === 'object' && 'error' in payload && payload.error) {
      const errObj = payload.error as { code?: string; message?: string; details?: unknown };
      return Promise.reject(
        new ApiError({
          code: errObj.code ?? 'UNKNOWN_ERROR',
          message: errObj.message ?? error.message ?? 'Erro desconhecido',
          status: status ?? 0,
          details: errObj.details,
        }),
      );
    }

    return Promise.reject(
      new ApiError({
        code: error.code ?? 'NETWORK_ERROR',
        message: error.message || 'Erro de conexão com o servidor',
        status: status ?? 0,
      }),
    );
  },
);

export const apiRequest = async <T>(config: AxiosRequestConfig): Promise<T> => {
  if (env.VITE_USE_MOCKS) {
    return handleMockRequest<T>(config);
  }
  const response = await apiClient.request<ApiResponse<T>>(config);
  const body = response.data;
  if (body && typeof body === 'object' && 'success' in body) {
    if (body.success) return body.data;
    throw new ApiError({
      code: body.error.code,
      message: body.error.message,
      status: response.status,
      details: body.error.details,
    });
  }
  return body as unknown as T;
};
